"use client";

import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebug } from "@huub-web/ui";

import { useTemplates } from "./useTemplates";
import {
  PageContentContext,
  PageContentProviderActive,
  type PageAction,
  type PageContentContextState,
  type PageContentItem,
} from "../contexts/PageContentContext";

type PageContentProviderProps = {
  /**
   * Child components (templates that register actions and content)
   */
  children: ReactNode;
};

/**
 * Provides PageContentContext to child templates and reports collected
 * actions and content items to the AI agent via the dedicated pageActions
 * field in TemplatesProvider (separate from the slice registry).
 *
 * Debouncing and WS reconnect are handled centrally by TemplatesProvider.
 * This component only calls setPageActions/clearPageActions when children change.
 */
export const PageContentProvider: FC<PageContentProviderProps> = ({
  children,
}) => {
  const { setPageActions, clearPageActions, confirmAction } = useTemplates();
  const { log } = useDebug();

  const actionsRef = useRef<Map<string, PageAction>>(new Map());
  const contentRef = useRef<Map<string, PageContentItem>>(new Map());
  const [version, setVersion] = useState(0);
  const batchPendingRef = useRef(false);

  /**
   * Coalesces multiple synchronous register/deregister calls into a
   * single state update via microtask batching. This prevents N separate
   * re-renders when N templates mount simultaneously.
   */
  const scheduleBatchUpdate = useCallback(() => {
    if (batchPendingRef.current) return;
    batchPendingRef.current = true;
    Promise.resolve().then(() => {
      batchPendingRef.current = false;
      setVersion((v) => v + 1);
    });
  }, []);

  const registerAction = useCallback(
    (id: string, action: PageAction) => {
      actionsRef.current.set(id, action);
      scheduleBatchUpdate();
    },
    [scheduleBatchUpdate],
  );

  const deregisterAction = useCallback(
    (id: string) => {
      if (actionsRef.current.delete(id)) {
        scheduleBatchUpdate();
      }
    },
    [scheduleBatchUpdate],
  );

  const registerContent = useCallback(
    (id: string, item: PageContentItem) => {
      contentRef.current.set(id, item);
      scheduleBatchUpdate();
    },
    [scheduleBatchUpdate],
  );

  const deregisterContent = useCallback(
    (id: string) => {
      if (contentRef.current.delete(id)) {
        scheduleBatchUpdate();
      }
    },
    [scheduleBatchUpdate],
  );

  const handleConfirmAction = useCallback(
    (actionName: string) => {
      confirmAction?.(actionName);
    },
    [confirmAction],
  );

  const contextValue = useMemo<PageContentContextState>(
    () => ({
      registerAction,
      deregisterAction,
      registerContent,
      deregisterContent,
      confirmAction: handleConfirmAction,
    }),
    [
      registerAction,
      deregisterAction,
      registerContent,
      deregisterContent,
      handleConfirmAction,
    ],
  );

  useEffect(() => {
    const actions = actionsRef.current;
    const content = contentRef.current;

    if (actions.size === 0 && content.size === 0) {
      clearPageActions?.();
      return;
    }

    const actionsArray =
      actions.size > 0
        ? Array.from(actions.values()).map((a) => ({
            label: a.label,
            type: a.action.type,
            payload: a.action.payload,
            destructive: a.destructive ?? false,
          }))
        : undefined;

    const contentArray =
      content.size > 0
        ? Array.from(content.values()).map((c) => ({
            type: c.type,
            label: c.label,
            ...(c.description ? { description: c.description } : {}),
            ...(c.meta ? { meta: c.meta } : {}),
            ...(c.action ? { action: c.action } : {}),
          }))
        : undefined;

    log(
      "brown",
      "PageContentProvider",
      `register (${actionsArray?.length ?? 0} actions, ${contentArray?.length ?? 0} content)`,
    );

    setPageActions?.({
      ...(actionsArray ? { actions: actionsArray } : {}),
      ...(contentArray ? { content: contentArray } : {}),
    });
  }, [version, setPageActions, clearPageActions, log]);

  useEffect(() => {
    return () => {
      clearPageActions?.();
    };
  }, [clearPageActions]);

  return (
    <PageContentProviderActive.Provider value={true}>
      <PageContentContext.Provider value={contextValue}>
        {children}
      </PageContentContext.Provider>
    </PageContentProviderActive.Provider>
  );
};
